// Comparação sem acento que preserva o tamanho em unidades UTF-16. Compartilhada
// pela busca de mensagens do chat e pela busca da Base de Conhecimento.

/**
 * Fold de diacríticos preservando o comprimento em UNIDADES DE CÓDIGO UTF-16.
 * NFD + remove diacríticos + lowercase, porém cada char do texto ORIGINAL ocupa,
 * no resultado, exatamente o mesmo número de code units UTF-16 (`ch.length`).
 *
 * Isso importa porque `indexOf`/`slice` indexam por code UNIT, não por code
 * point. Um caractere astral (ex.: emoji) tem 1 code point mas 2 code units;
 * se o fold emitisse 1 unit, todos os índices após ele derivariam 1 por emoji
 * e o destaque cairia nos caracteres errados.
 *
 * Ao padronizar cada chunk folded para `ch.length` code units (preenchendo com
 * ' ', que nunca casa, quando o fold encurta; truncando no caso raro em que
 * alonga), garantimos `dobrar(s).length === s.length`: indexOf sobre o dobrado
 * devolve um índice válido para `slice` no original.
 */
export function dobrar(s: string): string {
  let out = ''
  for (const ch of s) {
    let folded = ch.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
    // Alinha o chunk folded ao número de code units UTF-16 do char original.
    if (folded.length < ch.length) folded = folded.padEnd(ch.length, ' ')
    else if (folded.length > ch.length) folded = folded.slice(0, ch.length)
    out += folded
  }
  return out
}

export interface Pedaco {
  texto: string
  marcado: boolean
}

/** Corta `texto` em pedaços, marcando toda ocorrência (sem acento) de qualquer termo. */
export function destacar(texto: string, termos: string[]): Pedaco[] {
  const ts = termos.map((t) => dobrar(t.trim())).filter((t) => t.length > 0)
  if (!texto) return []
  if (ts.length === 0) return [{ texto, marcado: false }]
  const dobrado = dobrar(texto)
  const marca = new Array<boolean>(texto.length).fill(false)
  for (const t of ts) {
    for (let i = dobrado.indexOf(t); i >= 0; i = dobrado.indexOf(t, i + t.length)) {
      marca.fill(true, i, i + t.length)
    }
  }
  const pedacos: Pedaco[] = []
  let ini = 0
  for (let i = 1; i <= texto.length; i++) {
    if (i === texto.length || marca[i] !== marca[ini]) {
      pedacos.push({ texto: texto.slice(ini, i), marcado: marca[ini] })
      ini = i
    }
  }
  return pedacos
}
